export type {
  Prescription as StimHomePrescription,
  PrescriptionDefaultParam as StimHomePrescriptionDefaultParam,
  PrescriptionParam as StimHomePrescriptionParam,
  CreatePrescriptionRequest as StimHomePrescriptionCreateRequest,
  CancelPrescriptionRequest as StimHomeCancelPrescriptionRequest,
  CreateRequest as StimHomePrescriptionDefaultParamCreateRequest,
  UpdateRequest as StimHomePrescriptionDefaultParamUpdateRequest,
} from '../generated/stim-home';

export type { TherapyCheckInProgress as StimHomeTherapyProgress } from '../generated/stim-home';
export type { TherapyCheckIn as StimHomeCheckIn, StimSession as StimHomeSession } from '../generated/stim-home';

export {
  PrescriptionStatus as StimHomePrescriptionStatus,
  StimSessionResult as StimHomeSessionResult,
  CancelReason as StimHomeCancelReason,
  CheckInDiscomfort as StimHomeCheckInDiscomfort,
  CheckInCompletion as StimHomeCheckInCompletion,
} from '../generated/stim-home';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// 중복 접수(더블클릭·연타) 방지: 발송/생성/제출 mutation 은 진행 중이면 새 요청 안 보내고 진행중 Promise 반환.
import { useGuardedMutation as useMutation } from '../../hooks/useGuardedMutation';
import { API_CONFIG } from '../config/env';
import {
  PrescriptionControllerApi,
  StimSessionControllerApi,
  TherapyCheckInControllerApi,
  CreatePrescriptionRequest as StimHomePrescriptionCreateRequest,
  CancelReason as StimHomeCancelReason,
  Prescription as StimHomePrescription,
  PrescriptionDefaultParam as StimHomePrescriptionDefaultParam,
  UpdateRequest as StimHomePrescriptionDefaultParamUpdateRequest,
  CreateRequest as StimHomePrescriptionDefaultParamCreateRequest,
  StimSession as StimHomeSession,
  TherapyCheckInProgress as StimHomeTherapyProgress,
  TherapyCheckIn as StimHomeCheckIn,
} from '../generated/stim-home';
import {
  QK_STIM_HOME_ACTIVE_PRESCRIPTION_LIST,
  QK_STIM_HOME_CHECK_IN_HISTORY,
  QK_STIM_HOME_DEFAULT_STIMULATION_SETTING,
  QK_STIM_HOME_PRESCRIPTION_LIST,
  QK_STIM_HOME_STIM_SESSION_LIST,
  QK_STIM_HOME_THERAPY_PROGRESS,
} from '../query-keys';

const STIM_HOME_URL = API_CONFIG.STIM_HOME_URL;
const prescriptionControllerApi = new PrescriptionControllerApi(undefined, STIM_HOME_URL);
const stimSessionControllerApi = new StimSessionControllerApi(undefined, STIM_HOME_URL);
const therapyCheckInControllerApi = new TherapyCheckInControllerApi(undefined, STIM_HOME_URL);

export const useCreatePrescriptions = () => {
  const queryClient = useQueryClient();
  return useMutation<StimHomePrescription, AxiosError, StimHomePrescriptionCreateRequest>({
    networkMode: 'always',
    mutationFn: async (value) => {
      return prescriptionControllerApi.createPrescription(value).then((res) => res.data);
    },
    onSuccess: (_, value) =>
      queryClient.invalidateQueries({ queryKey: [QK_STIM_HOME_PRESCRIPTION_LIST, value.patientId] }),
  });
};

export const useDeletePrescription = () => {
  const queryClient = useQueryClient();
  return useMutation<void, any, { id: string }>({
    mutationFn: async ({ id }) => {
      return prescriptionControllerApi.deletePrescriptionById(id).then((res) => res.data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK_STIM_HOME_PRESCRIPTION_LIST] }),
  });
};

export const useCancelPrescription = () => {
  const queryClient = useQueryClient();
  return useMutation<
    StimHomePrescription,
    AxiosError,
    { id: string; reason: StimHomeCancelReason; reasonMemo?: string }
  >({
    networkMode: 'always',
    mutationFn: async ({ id, reason, reasonMemo }) => {
      return prescriptionControllerApi.cancel(id, { reason, reasonMemo }).then((res) => res.data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK_STIM_HOME_PRESCRIPTION_LIST] }),
  });
};

export const useFetchPrescriptions = (patientId?: string, placeholderData: boolean = true) => {
  return useQuery<StimHomePrescription[]>({
    queryKey: [QK_STIM_HOME_PRESCRIPTION_LIST, patientId],
    queryFn: async () => {
      return prescriptionControllerApi.findAllPrescriptionsByPatientId(patientId!).then((res) => res.data);
    },
    gcTime: 0,
    enabled: !!patientId,
    ...(placeholderData === false && { placeholderData: undefined }),
  });
};

export const useCreateDefaultStimulationSetting = () => {
  return useMutation<StimHomePrescriptionDefaultParam, AxiosError, StimHomePrescriptionDefaultParamCreateRequest>({
    mutationFn: async (value) => {
      return prescriptionControllerApi.createDefaultPrescriptionParam(value).then((res) => res.data);
    },
  });
};

export const useFetchDefaultStimulationSetting = () => {
  return useQuery<StimHomePrescriptionDefaultParam>({
    queryKey: [QK_STIM_HOME_DEFAULT_STIMULATION_SETTING],
    queryFn: async () => {
      return prescriptionControllerApi.findDefaultParam().then((res) => res.data);
    },
  });
};

export const useUpsertDefaultStimulationSetting = () => {
  const queryClient = useQueryClient();
  return useMutation<
    StimHomePrescriptionDefaultParam,
    AxiosError,
    { id: string; body: StimHomePrescriptionDefaultParamUpdateRequest }
  >({
    mutationFn: async ({ id, body }) => {
      return prescriptionControllerApi.updateDefaultPrescriptionParam(id, body).then((res) => res.data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK_STIM_HOME_DEFAULT_STIMULATION_SETTING] }),
  });
};

export const useFetchStimSession = (prescriptionId?: string) => {
  return useQuery<StimHomeSession[]>({
    queryKey: [QK_STIM_HOME_STIM_SESSION_LIST, prescriptionId],
    queryFn: async () => {
      return stimSessionControllerApi.findAllByPrescriptionId(prescriptionId!).then((res) => res.data);
    },
    enabled: !!prescriptionId,
    gcTime: 0,
  });
};

/**
 * 기관의 진행 중 처방 목록(GET /api/v1/prescriptions/active). orgId 는 JWT 스코프에서 결정.
 * includeClosedWithinDays: 최근 N일 내 종료(완료/취소)된 처방도 포함(대시보드 STIM 이용 현황은 7 로 호출).
 */
export const useFetchActivePrescriptions = (includeClosedWithinDays?: number) => {
  return useQuery<StimHomePrescription[]>({
    queryKey: [QK_STIM_HOME_ACTIVE_PRESCRIPTION_LIST, includeClosedWithinDays],
    queryFn: () =>
      prescriptionControllerApi.findActivePrescriptions(includeClosedWithinDays).then((res) => res.data),
    // STIM 이용 현황은 항상 최신 처방을 조회한다(캐시 미사용).
    gcTime: 0,
    staleTime: 0,
  });
};

/**
 * 처방 id 목록 → 처방별 치료 체크인 진행률(목표 횟수·작성 횟수) 벌크 조회(POST /api/v1/therapy-check-ins/progresses).
 * 존재하지 않는/다른 기관/삭제된 처방은 응답에서 제외(부분 성공)되므로 prescriptionId 로 매핑해 사용한다.
 */
export const useFetchTherapyProgresses = (prescriptionIds: string[]) => {
  return useQuery<StimHomeTherapyProgress[]>({
    queryKey: [QK_STIM_HOME_THERAPY_PROGRESS, prescriptionIds],
    queryFn: () => therapyCheckInControllerApi.findProgresses({ prescriptionIds }).then((res) => res.data),
    enabled: prescriptionIds.length > 0,
    // STIM 이용 현황은 항상 최신 치료 체크인 진행률을 조회한다(캐시 미사용).
    gcTime: 0,
    staleTime: 0,
  });
};

/**
 * 처방 1건의 치료 체크인 원본 내역(기관용, GET /prescriptions/{id}/check-ins). 최신 날짜순.
 * 격자·회차는 처방의 param.dates 와 조합해 프론트가 구성한다.
 */
export const useFetchTherapyCheckInHistory = (prescriptionId?: string) => {
  return useQuery<StimHomeCheckIn[]>({
    queryKey: [QK_STIM_HOME_CHECK_IN_HISTORY, prescriptionId],
    queryFn: () => therapyCheckInControllerApi.findHistory1(prescriptionId!).then((res) => res.data),
    enabled: !!prescriptionId,
    // 환자가 앱 밖에서 작성한 기록이라 갱신 신호가 없다 — 전역 기본 캐시(3시간)를 끈다.
    gcTime: 0,
    staleTime: 0,
  });
};
